import React, { Suspense } from 'react'
import { Routes, Route, Outlet, Navigate } from 'react-router-dom'
import FallbackSpinner from './@core/components/spinner'
import AuthGuard from 'src/@core/components/auth/AuthGuard'
import BlankLayout from './@core/layouts/BlankLayout'
import AclGuard from './@core/components/auth/AclGuard'
import GuestGuard from './@core/components/auth/GuestGuard'
import { defaultACLObj } from './configs/acl'
import PublicLayout from './layouts/PublicLayout'
import CookieConsentBanner from 'src/components/CookieConsentBanner'
import GoodsCategoryLabelsBootstrap from 'src/components/GoodsCategoryLabelsBootstrap'

const Page401 = React.lazy(() => import('./pages/401'))
const Page404 = React.lazy(() => import('./pages/404'))

// Page Imports
const LandingPage = React.lazy(() => import('./pages/landingPage'))
const SubscriptionPage = React.lazy(() => import('./pages/subscription-page'))
const HowItWorksPage = React.lazy(() => import('./pages/howitworks'))
const NewlyAddedServicePage = React.lazy(() => import('./pages/newly-added-service'))
const NewlyRegisteredAgentsPage = React.lazy(() => import('./pages/newly-registered-agents'))
const InternationalRoutesPage = React.lazy(() => import('./pages/international-routes'))
const DomesticRoutesPage = React.lazy(() => import('./pages/domestic-routes'))
const InternationalAgentsPage = React.lazy(() => import('./pages/international-agents'))
const DomesticAgentsPage = React.lazy(() => import('./pages/domestic-agents'))
const WorldWideDirectoryPage = React.lazy(() => import('./pages/worldwide-directory'))
const CountryWiseDirectoryPage = React.lazy(() => import('./pages/countrywise-directory'))
const CompanyDetailPage = React.lazy(() => import('./pages/companydata'))
const CompanyFrontPage = React.lazy(() => import('./pages/companyfrontpage'))
const OujatDigitalWalletPage = React.lazy(() => import('./pages/oujat-wallet'))
const PoliciesRulesPage = React.lazy(() => import('./pages/policies-rules'))
const LegalNoticePage = React.lazy(() => import('./pages/legal-notice'))
const ServiceListingPolicyPage = React.lazy(() => import('./pages/service-listing-policy'))
const PrivacyPolicyPage = React.lazy(() => import('./pages/privacy-policy'))
const TermsOfUsePage = React.lazy(() => import('./pages/terms-of-use'))
const AboutUsPage = React.lazy(() => import('./pages/aboutus'))
const ContactUsPage = React.lazy(() => import('./pages/contact-us'))
const SecuredPaymentsPage = React.lazy(() => import('./pages/securedpayments'))
const AgentCategories = React.lazy(() => import('./pages/agentcategories'))
const CustomerTypes = React.lazy(() => import('./pages/customerType'))
const OujatReadsPage = React.lazy(() => import('./pages/oujatreads'))
const PopularBlogs = React.lazy(() => import('./pages/readsblogs'))
const OujatCareerPage = React.lazy(() => import('./pages/oujatcareer'))
const CurrentOpeningJobs = React.lazy(() => import('./pages/currentopeningjobs'))
const CareerDetailsPage = React.lazy(() => import('./pages/careerdetailpage'))
const HelpCenterPage = React.lazy(() => import('./pages/help-center'))
const SubHelpCenterPage = React.lazy(() => import('./pages/help-center/sub-help-center'))
const HelpCenterDetailPage = React.lazy(() => import('./pages/help-center/detail'))
const ServiceCategoriesPage = React.lazy(() => import('./pages/servicecategoriespage'))
const AllTransportServicesPage = React.lazy(() => import('./pages/allservices'))
const CompareServicesPage = React.lazy(() => import('./pages/compareservicespage'))
const WithProtectionDetaiPage = React.lazy(() => import('./pages/withprotectiondetaillpage'))
const WithoutProtectionDetaiPage = React.lazy(() => import('./pages/withoutprotectiondetaillpage'))
const SimilarServiceDetailPage = React.lazy(() => import('./pages/similarServiceDetailsPage'))

const Guard = ({ children, authGuard, guestGuard }) => {
  if (guestGuard) {
    return <GuestGuard fallback={<FallbackSpinner />}>{children}</GuestGuard>
  } else if (!guestGuard && !authGuard) {
    return <>{children}</>
  } else {
    return <AuthGuard fallback={<FallbackSpinner />}>{children}</AuthGuard>
  }
}

function App() {
  const aclAbilities = defaultACLObj

  return (
    <>
      <Suspense fallback={<FallbackSpinner />}>
        <AclGuard aclAbilities={aclAbilities}>
          <Routes>
          <Route
            element={
              <BlankLayout>
                <Outlet />
              </BlankLayout>
            }
          >
            <Route path='/401' element={<Page401 />} />
            <Route path='/404' element={<Page404 />} />
          </Route>
          <Route
            element={
              <PublicLayout>
                <Outlet />
              </PublicLayout>
            }
          >
            <Route
              element={
                <Guard guestGuard>
                  <Outlet />
                </Guard>
              }
            >
              <Route path='/landing-page' element={<LandingPage />} />
              <Route path='/subscription-page' element={<SubscriptionPage />} />
              <Route path='/how-it-works' element={<HowItWorksPage />} />
              <Route path='/newly-added-servicepage' element={<NewlyAddedServicePage />} />
              <Route path='/newly-registered-agents' element={<NewlyRegisteredAgentsPage />} />
              <Route path='/international-routes-page' element={<InternationalRoutesPage />} />
              <Route path='/domestic-routes-page' element={<DomesticRoutesPage />} />
              <Route path='/domestic-agents-page' element={<DomesticAgentsPage />} />
              <Route path='/international-agents-page' element={<InternationalAgentsPage />} />
              <Route path='/worldwide-directory' element={<WorldWideDirectoryPage />} />
              <Route path='/country-directory-page/:countryName' element={<CountryWiseDirectoryPage />} />
              <Route path='/company-details/:countryName/:stateName' element={<CompanyDetailPage />} />
              <Route path='/storefront/:userId' element={<CompanyFrontPage />} />
              <Route path='/oujat-digital-wallet-page' element={<OujatDigitalWalletPage />} />
              <Route path='/policies-rules-page' element={<PoliciesRulesPage />} />
              <Route path='/legal-notice-page' element={<LegalNoticePage />} />
              <Route path='/service-listing-policy-page' element={<ServiceListingPolicyPage />} />
              <Route path='/privacy-policy-page' element={<PrivacyPolicyPage />} />
              <Route path='/terms-of-use-page' element={<TermsOfUsePage />} />
              <Route path='/aboutus' element={<AboutUsPage />} />
              <Route path='/contact-us' element={<ContactUsPage />} />
              <Route path='/securedpayments' element={<SecuredPaymentsPage />} />
              <Route path='/agentcategories' element={<AgentCategories />} />
              <Route path='/customerstype' element={<CustomerTypes />} />
              <Route path='/oujatreads' element={<OujatReadsPage />} />
              <Route path='/popularblogs/:id' element={<PopularBlogs />} />
              <Route path='/career' element={<OujatCareerPage />} />
              <Route path='/apply-jobs' element={<CurrentOpeningJobs />} />
              <Route path='/jobs-details/' element={<CareerDetailsPage />} />
              <Route path='/help-center' element={<HelpCenterPage />} />
              <Route path='/help-center/sub-help' element={<SubHelpCenterPage />} />
              <Route path='/help-center/sub-help/:id' element={<HelpCenterDetailPage />} />
              <Route path='/categories/:category' element={<ServiceCategoriesPage />} />
              <Route path='/allServices' element={<AllTransportServicesPage />} />
              <Route path='/compare-services' element={<CompareServicesPage />} />
              <Route path='/with-oujat-protection/:id' element={<WithProtectionDetaiPage />} />
              <Route path='/without-oujat-protection/:id' element={<WithoutProtectionDetaiPage />} />
              <Route path='/similar-services-details/:id' element={<SimilarServiceDetailPage />} />
            </Route>
          </Route>

          {/* If no route found redirect it to */}
          <Route path='*' element={<Navigate to='/404' replace />} />
          </Routes>
        </AclGuard>
      </Suspense>
      <CookieConsentBanner privacyHref='/privacy-policy-page' />
      <GoodsCategoryLabelsBootstrap />
    </>
  )
}

export default App
